import type { FC, MouseEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useIntercom } from 'react-use-intercom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { X, Stars01 } from '@untitled-ui/icons-react'
import SvgIcon from '@mui/material/SvgIcon'
import useTranslate from '@/hooks/use-translate'
import type { Release } from '../types'
import {
  whatsNewBackdropSx,
  whatsNewBodySx,
  whatsNewCloseButtonSx,
  whatsNewEmptyTextSx,
  whatsNewFooterSx,
  whatsNewFooterTextSx,
  whatsNewHeaderIconSx,
  whatsNewHeaderSx,
  whatsNewPaperSx,
  whatsNewSubtitleSx,
  whatsNewTitleSx,
  whatsNewUnderstoodButtonSx,
} from '../whatsNewModalStyles'
import { ReleasesCarousel } from './ReleasesCarousel'

type WhatsNewModalProps = {
  open: boolean
  releases: Release[]
  isLoading?: boolean
  isEmpty: boolean
  onClose: () => void
  /** Appelé uniquement au clic sur « Compris » — déclenche POST /acknowledge. */
  onAcknowledge: (release: Release) => void | Promise<void>
}

export const WhatsNewModal: FC<WhatsNewModalProps> = ({
  open,
  releases,
  isLoading = false,
  isEmpty,
  onClose,
  onAcknowledge,
}) => {
  const { t } = useTranslate('whatsNew')
  const { t: tCommon } = useTranslate('common')
  const { show: showIntercom } = useIntercom()
  const [currentRelease, setCurrentRelease] = useState<Release | null>(null)
  const [carouselKey, setCarouselKey] = useState(0)

  useEffect(() => {
    if (open) {
      setCarouselKey((k) => k + 1)
      setCurrentRelease(releases[0] ?? null)
    }
  }, [open, releases])

  const handleDismiss = useCallback(() => {
    onClose()
  }, [onClose])

  const handleContactSupport = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      showIntercom()
    },
    [showIntercom],
  )

  const handleUnderstood = useCallback(async () => {
    const release = currentRelease ?? releases[0]
    try {
      if (release) {
        await onAcknowledge(release)
      }
    } finally {
      onClose()
    }
  }, [currentRelease, releases, onAcknowledge, onClose])

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleDismiss()
        }
      }}
      maxWidth={false}
      fullWidth
      slotProps={{
        backdrop: {
          sx: whatsNewBackdropSx,
        },
      }}
      PaperProps={{
        sx: whatsNewPaperSx,
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={whatsNewHeaderSx}>
          <Box sx={whatsNewHeaderIconSx}>
            <SvgIcon>
              <Stars01 />
            </SvgIcon>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={whatsNewTitleSx}>{t('menuTitle')}</Typography>
            <Typography sx={whatsNewSubtitleSx}>{t('dialogSubtitle')}</Typography>
          </Box>
          <Button
            onClick={handleDismiss}
            aria-label={tCommon('close')}
            sx={whatsNewCloseButtonSx}
          >
            <SvgIcon>
              <X />
            </SvgIcon>
          </Button>
        </Box>

        <Box sx={whatsNewBodySx}>
          {isLoading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : isEmpty ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
              }}
            >
              <Typography sx={whatsNewEmptyTextSx}>{t('emptyState')}</Typography>
            </Box>
          ) : (
            <ReleasesCarousel
              key={carouselKey}
              releases={releases}
              onCurrentReleaseChange={setCurrentRelease}
            />
          )}
        </Box>

        <Box sx={whatsNewFooterSx}>
          <Typography sx={whatsNewFooterTextSx}>
            {t('footerQuestion')}{' '}
            <Box
              component="a"
              href="#"
              onClick={handleContactSupport}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {t('footerContact')}
            </Box>
          </Typography>
          <Button
            onClick={handleUnderstood}
            disabled={isEmpty}
            sx={whatsNewUnderstoodButtonSx}
          >
            {t('understood')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
